import { useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { cameraAvailable, unlockBeep } from './camera-scan';
import { CameraScanDialog, type CodeHandler } from './CameraScanDialog';

/** CAM-01: a secure page with camera access, and a camera listed (none on most desktops). */
function useHasCamera() {
  const [has, setHas] = useState(cameraAvailable);
  useEffect(() => {
    if (!has) return;
    let live = true;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (live && !devices.some((device) => device.kind === 'videoinput')) setHas(false);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [has]);
  return has;
}

/** The camera beside a scan field: each code it reads goes where a scanner's Enter goes. */
export function CameraScanButton({
  onCode,
  className,
}: {
  onCode: CodeHandler;
  className?: string;
}) {
  const { t } = useTranslation();
  const hasCamera = useHasCamera();
  const [open, setOpen] = useState(false);
  if (!hasCamera) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          unlockBeep();
          setOpen(true);
        }}
        aria-label={t('camera.open')}
        title={t('camera.open')}
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-control text-primary hover:bg-primary-soft',
          className,
        )}
      >
        <Camera className="size-6" />
      </button>
      <CameraScanDialog open={open} onClose={() => setOpen(false)} onCode={onCode} />
    </>
  );
}
